import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Pagination, Badge } from 'react-bootstrap'
import client from '../../api/client'

function AuditLogs() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({
    dateFrom: getDefaultDateFrom(),
    dateTo: getDefaultDateTo(),
    action: '',
    admin: '',
  })
  const perPage = 25

  function getDefaultDateFrom() {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().split('T')[0]
  }

  function getDefaultDateTo() {
    return new Date().toISOString().split('T')[0]
  }

  useEffect(() => {
    fetchLogs()
  }, [page])

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      if (filters.action) params.append('action', filters.action)
      if (filters.admin) params.append('admin_id', filters.admin)
      params.append('limit', perPage)
      params.append('offset', (page - 1) * perPage)

      const response = await client.get(`/admin/api/v1/logs/audit?${params}`)
      setLogs(response.data.logs || response.data || [])
      setTotal(response.data.total || 0)
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setError('Erreur lors du chargement des logs')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    setPage(1)
    fetchLogs()
  }

  const getActionBadge = (action) => {
    const colors = {
      create: 'success',
      update: 'primary',
      delete: 'danger',
      login: 'info',
      logout: 'secondary',
    }
    return <Badge bg={colors[action] || 'secondary'}>{action}</Badge>
  }

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'medium',
    })
  }

  const totalPages = Math.ceil(total / perPage)

  const actionOptions = ['create', 'update', 'delete', 'login', 'logout', 'sync', 'export']

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Logs d'audit</h1>
          <p className="text-muted">
            Consultez les actions des administrateurs sur la plateforme
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Header>Filtres</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Action</Form.Label>
                <Form.Select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  <option value="">Toutes</option>
                  {actionOptions.map(action => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Administrateur (ID)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filtrer par admin..."
                  value={filters.admin}
                  onChange={(e) => handleFilterChange('admin', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button variant="primary" onClick={handleApplyFilters} className="w-100">
                Appliquer
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Historique des actions</span>
          <span className="text-muted">{total} entrées</span>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>Aucun log trouvé</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Administrateur</th>
                    <th>Action</th>
                    <th>Ressource</th>
                    <th>Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="text-nowrap">{formatTimestamp(log.created_at)}</td>
                      <td>{log.admin_email || log.admin_id || '-'}</td>
                      <td>{getActionBadge(log.action)}</td>
                      <td>
                        <code>{log.resource_type || '-'}</code>
                        {log.resource_id && (
                          <span className="ms-1 text-muted">#{log.resource_id}</span>
                        )}
                      </td>
                      <td>
                        {log.details && (
                          <small className="text-muted">
                            {typeof log.details === 'object'
                              ? JSON.stringify(log.details).substring(0, 100)
                              : log.details.substring(0, 100)}
                            {(log.details.length || JSON.stringify(log.details).length) > 100 && '...'}
                          </small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                    <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                      const pageNum = i + 1
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={page === pageNum}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      )
                    })}
                    {totalPages > 5 && <Pagination.Ellipsis />}
                    <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages} />
                    <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default AuditLogs
